/*
  File: src/pages/CartPage.jsx
  Purpose: Cart and checkout page with address capture, payment selection, and order placement.
  Responsibilities:
  - Render editable cart items with quantity and removal controls.
  - Persist and edit default shipping address via /shipping-addresses.
  - Choose payment method and place an order via /orders/checkout, then clear cart.
*/
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCreditCard } from '@fortawesome/free-solid-svg-icons';
import { faPaypal, faBitcoin } from '@fortawesome/free-brands-svg-icons';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useCart } from '../cart/CartContext';
import { useAuth } from '../auth/AuthContext';
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../auth/api';

export default function CartPage() {
  const { items, updateQty, removeItem, clear, total, paymentMethod, setPaymentMethod } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [addr, setAddr] = useState({ full_name: '', line1: '', line2: '', city: '', state: '', postal_code: '', country: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [cardLast4, setCardLast4] = useState('4242');
  const [cardBrand, setCardBrand] = useState('Visa');

  useEffect(() => {
    // Simulate a stored card brand/last4 for demo purposes
    const brands = ['Visa', 'Mastercard', 'Amex'];
    const tails = ['4242', '1337', '1111', '8888'];
    setCardBrand(brands[Math.floor(Math.random() * brands.length)]);
    setCardLast4(tails[Math.floor(Math.random() * tails.length)]);
  }, []);
  const [successOpen, setSuccessOpen] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState('card');
  const [cryptoOpen, setCryptoOpen] = useState(false);
  const [cryptoInvoice, setCryptoInvoice] = useState(null);
  const [cryptoAmountLoading, setCryptoAmountLoading] = useState(false);
  const [btcPriceUsd, setBtcPriceUsd] = useState(null);
  const [btcPriceLoading, setBtcPriceLoading] = useState(false);
  const [btcPriceError, setBtcPriceError] = useState('');

  // Fetch current BTC price in USD when crypto is selected
  useEffect(() => {
    const loadBtcPrice = async () => {
      if (!selectedProvider.toLowerCase().includes('crypto')) return;
      try {
        setBtcPriceLoading(true);
        setBtcPriceError('');
        const { data } = await apiClient.get('/crypto/btc/price');
        const price = Number(data?.price_usd);
        if (Number.isFinite(price) && price > 0) {
          setBtcPriceUsd(price);
        } else {
          setBtcPriceUsd(null);
          setBtcPriceError('Unable to load BTC price');
        }
      } catch (e) {
        setBtcPriceUsd(null);
        setBtcPriceError('Failed to fetch BTC price');
      } finally {
        setBtcPriceLoading(false);
      }
    };
    loadBtcPrice();
  }, [selectedProvider]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get('/shipping-addresses');
        if (data && data.item) setAddr({
          full_name: data.item.full_name || '',
          line1: data.item.line1 || '',
          line2: data.item.line2 || '',
          city: data.item.city || '',
          state: data.item.state || '',
          postal_code: data.item.postal_code || '',
          country: data.item.country || '',
          phone: data.item.phone || '',
        });
        const any = Object.values(data?.item || {}).some(v => String(v || '').trim());
        setShowAddressForm(!any);
      } catch { }
    })();
  }, []);

  // Load payment providers dynamically (card, paypal, crypto)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get('/payment-providers');
        const items = Array.isArray(data?.items) ? data.items : [];
        const normalized = items
          .map(p => ({ id: p.id, name: String(p.name || '').trim() }))
          .filter(p => p.name)
          .filter(p => !p.name.toLowerCase().includes('stripe'));
        // Prefer display order: card, PayPal, Crypto; otherwise alphabetical
        const order = ['card', 'paypal', 'crypto'];
        normalized.sort((a, b) => {
          const ia = order.indexOf(a.name.toLowerCase());
          const ib = order.indexOf(b.name.toLowerCase());
          if (ia !== -1 || ib !== -1) return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
          return a.name.localeCompare(b.name);
        });
        if (normalized.length === 0) {
          const fallback = [
            { id: 2, name: 'PayPal' },
            { id: 3, name: 'Crypto' },
          ];
          setProviders(fallback);
          setSelectedProvider('PayPal');
          setPaymentMethod('paypal');
        } else {
          setProviders(normalized);
          const preferred = normalized[0];
          if (preferred) {
            setSelectedProvider(preferred.name);
            setPaymentMethod(preferred.name.toLowerCase());
          }
        }
      } catch {
        // Fallback demo providers
        const fallback = [
          { id: 1, name: 'card' },
          { id: 2, name: 'PayPal' },
          { id: 3, name: 'Crypto' },
        ];
        setProviders(fallback);
        setSelectedProvider('card');
        setPaymentMethod('card');
      }
    })();
  }, [setPaymentMethod]);

  const saveAddress = async () => {
    try {
      setSaving(true);
      await apiClient.post('/shipping-addresses', addr);
      setShowAddressForm(false);
    } catch { } finally { setSaving(false); }
  };

  const addressSummary = useMemo(() => {
    const parts = [
      addr.full_name,
      addr.line1,
      addr.line2,
      [addr.city, addr.state].filter(Boolean).join(', '),
      [addr.postal_code, addr.country].filter(Boolean).join(' '),
    ].filter(v => String(v || '').trim());
    return parts.join(' • ');
  }, [addr]);

  const placeOrder = async () => {
    if (!user || items.length === 0) return;
    try {
      setPlacing(true);
      setStatusMsg('');
      // 1. When syncing cart before order, always send variant_id (not just product_id) for correctness
      try {
        await apiClient.post('/carts/sync', {
          user_id: user.id,
          items: items.map(i => ({ product_id: i.id, variant_id: i.variantId || null, qty: i.qty, size: i.size })),
        });
      } catch { }
      // Fetch latest cart for user to get cart_id
      const cartsRes = await apiClient.get('/carts', { params: { user_id: user.id, limit: 1, offset: 0 } });
      const latest = cartsRes.data?.items?.[0];
      const cart_id = latest ? latest.id : null;
      // Create order
      const order_uid = `ORD-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
      // Provider name from selection; backend will resolve/create provider internally
      const providerName = selectedProvider || (paymentMethod === 'paypal' ? 'PayPal' : paymentMethod === 'crypto' ? 'Crypto' : 'Card');

      // 2. In the order/checkout POST, variant_id must always be included in items
      const oRes = await apiClient.post('/orders/checkout', {
        user_id: user.id,
        cart_id,
        order_uid,
        status: 'pending',
        items: items.map(i => ({ product_id: i.id, variant_id: i.variantId || null, qty: i.qty, price_cents: Math.round((i.price || 0) * 100), currency: i.currency })),
        payment: {
          provider_name: providerName,
          provider_payment_id: order_uid,
          amount_cents: Math.round(total * 100),
          currency: items[0]?.currency || 'EUR',
          status: 'pending',
        },
      });
      const createdOrderId = oRes.data?.id;
      const paymentId = oRes.data?.payment?.id;
      setOrderId(createdOrderId || null);

      // Update order status to completed and payment status to done after successful payment
      if (createdOrderId) {
        try {
          await apiClient.put(`/orders/${createdOrderId}`, { status: 'completed' });
          console.log('Order status updated to completed');

          // Update payment status to done if payment exists
          if (paymentId) {
            await apiClient.put(`/payments/${paymentId}`, { status: 'done' });
            console.log('Payment status updated to done');
          }
        } catch (e) {
          console.error('Failed to update order/payment status:', e);
        }
      }

      // Clear cart locally after creating order
      clear();
      setSuccessOpen(true);
    } catch (e) {
      // swallow
      setStatusMsg('Failed to place order. Please try again.');
    } finally {
      setPlacing(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col pt-14 sm:pt-16">
      <Navbar />
      <main className="flex-1 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 w-full">
        <h1 className="text-2xl font-bold mb-4">Your Cart</h1>
        {items.length === 0 && <div className="text-white/70">Your cart is empty.</div>}
        {items.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="md:col-span-2 space-y-3">
              {items.map(i => (
                <div key={`${i.id}-${i.size || ''}-${i.qty}-${i.price}`} className="group flex gap-4 items-center bg-white/5 border border-white/10 rounded-2xl p-4 hover:bg-white/10 hover:border-white/20 transition-all duration-300">
                  <div className="w-20 h-24 bg-gradient-to-br from-slate-600 to-slate-700 rounded-xl overflow-hidden relative">
                    {i.imageUrl ? <img src={i.imageUrl} alt={i.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" /> : null}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white truncate">{i.name || i.name}</div>
                    <div className="text-xs text-white/70 truncate">{i.color ? `Color: ${i.color} • ` : ''}Size: {i.size}</div>
                    <div className="mt-1 text-sm text-white font-semibold">{i.currency} {(i.price * i.qty).toFixed(2)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input type="number" min={1} value={i.qty} onChange={(e) => updateQty(i.id, i.size, i.color || null, i.variantId || null, Number(e.target.value))} className="w-20 px-3 py-2 rounded-full bg-black/40 border border-white/10 focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/30 outline-none" />
                    <button onClick={() => removeItem(i.id, i.size || null, i.color || null, i.variantId || null)} className="group border-2 border-white/20 hover:border-white/40 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 transform hover:scale-105">Remove</button>
                  </div>
                </div>
              ))}
              <button onClick={clear} className="mt-2 group border-2 border-white/20 hover:border-white/40 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-5 py-2 rounded-full font-semibold text-sm transition-all duration-300 w-max">Clear Cart</button>
            </div>

            <div className="relative rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur-xl shadow-2xl h-max space-y-5">
              <h2 className="text-lg font-bold mb-3">Checkout</h2>
              <div>
                <div className="text-sm text-white/80 mb-2">Shipping address</div>
                {!showAddressForm ? (
                  <button
                    type="button"
                    onClick={() => setShowAddressForm(true)}
                    className="w-full text-left mb-3 px-3 py-2 rounded-lg bg-black/30 border border-white/10 hover:bg-black/40 transition flex items-center justify-between gap-2"
                    title={addressSummary || '—'}
                  >
                    <span className="truncate text-sm text-white/90">{addressSummary || '—'}</span>
                    <span className="text-white/70 inline-flex items-center gap-1 text-xs">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                        <path d="M15.41 2.59a2 2 0 0 1 0 2.82l-9.5 9.5a2 2 0 0 1-.88.5l-3.04.76a.75.75 0 0 1-.91-.91l.76-3.04c.1-.36.28-.69.53-.95l9.5-9.5a2 2 0 0 1 2.82 0Zm-3.53 3.18-7.72 7.72c-.06.06-.1.13-.12.21l-.44 1.76 1.76-.44c.08-.02.15-.06.21-.12l7.72-7.72-1.41-1.41Zm2.12-2.12-.71.71 1.41 1.41.71-.71a1 1 0 1 0-1.41-1.41Z" />
                      </svg>
                      Edit
                    </span>
                  </button>
                ) : (
                  <div className="grid grid-cols-1 gap-2 mb-3">
                    <input className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/30 outline-none" placeholder="Full name" value={addr.full_name} onChange={(e) => setAddr(a => ({ ...a, full_name: e.target.value }))} />
                    <input className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/30 outline-none" placeholder="Address line 1" value={addr.line1} onChange={(e) => setAddr(a => ({ ...a, line1: e.target.value }))} />
                    <input className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/30 outline-none" placeholder="Address line 2 (optional)" value={addr.line2} onChange={(e) => setAddr(a => ({ ...a, line2: e.target.value }))} />
                    <div className="grid grid-cols-2 gap-2">
                      <input className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/30 outline-none" placeholder="City" value={addr.city} onChange={(e) => setAddr(a => ({ ...a, city: e.target.value }))} />
                      <input className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/30 outline-none" placeholder="State/Province" value={addr.state} onChange={(e) => setAddr(a => ({ ...a, state: e.target.value }))} />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <input className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/30 outline-none" placeholder="Postal code" value={addr.postal_code} onChange={(e) => setAddr(a => ({ ...a, postal_code: e.target.value }))} />
                      <input className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/30 outline-none" placeholder="Country" value={addr.country} onChange={(e) => setAddr(a => ({ ...a, country: e.target.value }))} />
                    </div>
                    <input className="px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-cyan-400/40 focus:ring-2 focus:ring-cyan-400/30 outline-none" placeholder="Phone (optional)" value={addr.phone} onChange={(e) => setAddr(a => ({ ...a, phone: e.target.value }))} />
                    <div className="text-xs text-white/60">Edits here will be used for this checkout.</div>
                    <div className="flex items-center gap-2">
                      <button onClick={saveAddress} disabled={saving} className={`group border-2 ${saving ? 'border-white/10 text-white/40' : 'border-white/20 hover:border-white/40'} bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 w-max`}>{saving ? 'Saving…' : 'Save'}</button>
                      <button type="button" onClick={() => setShowAddressForm(false)} className="group border-2 border-white/20 hover:border-white/40 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full font-semibold text-sm transition-all duration-300 w-max">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
              <div className="mb-3">
                <div className="text-sm text-white/80 mb-1">Payment Method</div>
                <div className="flex gap-4 items-start flex-wrap">
                  {providers.map((p) => {
                    const key = p.name.toLowerCase();
                    const label = p.name.charAt(0).toUpperCase() + p.name.slice(1);
                    const isActive = selectedProvider.toLowerCase() === key;
                    const iconDef = key.includes('card') ? faCreditCard : key.includes('paypal') ? faPaypal : key.includes('crypto') ? faBitcoin : faCreditCard;
                    const iconColor = key.includes('card') ? '#2563eb' /* indigo-600 */
                      : key.includes('paypal') ? '#003087' /* PayPal brand blue */
                        : key.includes('crypto') ? '#F7931A' /* Bitcoin orange */
                          : '#2563eb';
                    return (
                      <button
                        key={p.id || p.name}
                        onClick={() => { setSelectedProvider(p.name); setPaymentMethod(key); }}
                        className={`${isActive ? 'border-indigo-500 ring-2 ring-indigo-500/40' : 'border-white/20 hover:border-white/40'} group relative border-2 rounded-md p-3 flex flex-col items-center justify-center transition`}
                        aria-label={`Pay with ${label}`}
                        aria-pressed={isActive}
                      >
                        <span className={`w-14 h-9 flex items-center justify-center bg-white rounded-sm overflow-hidden transition-transform ${isActive ? '' : 'opacity-80 group-hover:opacity-100'} group-hover:scale-105`}>
                          <FontAwesomeIcon icon={iconDef} size="lg" style={{ color: iconColor }} />
                        </span>
                        <span className="mt-2 text-xs text-white/80 group-hover:text-white capitalize">{label}</span>
                      </button>
                    );
                  })}
                </div>
                {selectedProvider.toLowerCase().includes('card') && (
                  <div className="mt-2 text-xs text-white/80 flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white/10 border border-white/20">
                      <span className="text-white/90">{cardBrand}</span>
                      <span className="text-white/60">•••• {cardLast4}</span>
                    </span>
                    <span className="text-white/50">Saved card</span>
                  </div>
                )}
                {selectedProvider.toLowerCase().includes('paypal') && (
                  <div className="mt-2 text-xs text-white/80 flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-white/10 border border-white/20">
                      <span className="text-white/60">PayPal account:</span>
                      <span className="text-white/90 font-mono">{user?.email || 'user@example.com'}</span>
                    </span>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-white/80">Subtotal</span>
                <span className="font-semibold text-white">${total.toFixed(2)}</span>
              </div>
              {statusMsg && (
                <div className={`mt-2 text-xs ${statusMsg.includes('success') ? 'text-green-400' : 'text-red-400'}`}>{statusMsg}{orderId ? ` ID #${orderId}` : ''}</div>
              )}
              <button onClick={() => setConfirmOpen(true)} disabled={items.length === 0 || placing} className={`mt-4 w-full ${items.length === 0 || placing ? 'opacity-60 cursor-not-allowed' : 'group'} relative bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white px-6 py-3 rounded-full font-semibold text-lg transition-all duration-300 ${items.length === 0 || placing ? '' : 'transform hover:scale-105 hover:shadow-xl'}`}>{placing ? 'Placing…' : 'Place Order'}</button>
            </div>
          </div>
        )}
      </main>
      <Footer />

      {confirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="w-full max-w-md card p-4">
            <h3 className="text-lg font-bold mb-2">Confirm your order</h3>
            <div className="text-sm text-white/80 mb-4">
              You are about to place an order of {items.length} item(s) with subtotal ${total.toFixed(2)}.
            </div>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => { setConfirmOpen(false); setStatusMsg('Order cancelled.'); }} className="group border-2 border-white/20 hover:border-white/40 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-5 py-2 rounded-full font-semibold text-sm transition-all duration-300">Cancel</button>
              <button onClick={async () => {
                if (selectedProvider.toLowerCase().includes('crypto')) {
                  setConfirmOpen(false);
                  setCryptoAmountLoading(true);
                  const demoAddr = 'bc1qdemoaddress000xyz...';
                  const network = 'Bitcoin (Testnet)';
                  const usdTotal = Number(total);
                  setCryptoInvoice({ address: demoAddr, network, amount: null, currency: 'BTC', price_usd: btcPriceUsd ?? null, usd_total: usdTotal });
                  setCryptoOpen(true);
                  try {
                    let price = btcPriceUsd;
                    if (!Number.isFinite(price) || price <= 0) {
                      const { data } = await apiClient.get('/crypto/btc/price');
                      const fetched = Number(data?.price_usd);
                      price = Number.isFinite(fetched) && fetched > 0 ? fetched : null;
                    }
                    const btcAmount = price ? usdTotal / price : 0;
                    setCryptoInvoice({ address: demoAddr, network, amount: btcAmount.toFixed(8), currency: 'BTC', price_usd: price, usd_total: usdTotal });
                  } catch (e) {
                    setCryptoInvoice({ address: demoAddr, network, amount: '0.00000000', currency: 'BTC', price_usd: null, usd_total: usdTotal });
                  } finally {
                    setCryptoAmountLoading(false);
                  }
                } else {
                  setConfirmOpen(false);
                  await placeOrder();
                }
              }} className="group relative bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white px-5 py-2 rounded-full font-semibold text-sm transition-all duration-300 transform hover:scale-105 hover:shadow-xl">Confirm</button>
            </div>
          </div>
        </div>
      )}

      {successOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="w-full max-w-md card p-4">
            <h3 className="text-lg font-bold mb-2">Order placed</h3>
            <div className="text-sm text-white/80 mb-4">
              Your order has been created successfully{orderId ? <> (ID #{orderId})</> : null}. Do you want to confirm?
            </div>
            <div className="flex items-center justify-end gap-2">
              <button onClick={() => { setSuccessOpen(false); setStatusMsg(''); }} className="group border-2 border-white/20 hover:border-white/40 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-5 py-2 rounded-full font-semibold text-sm transition-all duration-300">Close</button>
              <button onClick={() => { setSuccessOpen(false); setStatusMsg(`Order placed successfully.${orderId ? ' ID #' + orderId : ''}`); navigate('/orders'); }} className="group relative bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white px-5 py-2 rounded-full font-semibold text-sm transition-all duration-300 transform hover:scale-105 hover:shadow-xl">Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Crypto payment simulation modal */}
      {cryptoOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="w-full max-w-md card p-4 space-y-3">
            <h3 className="text-lg font-bold">Crypto Payment</h3>
            <div className="text-sm text-white/80">Send payment to the address below. This is a demo flow.</div>
            <div className="rounded-lg border border-white/10 bg-white/5 p-3">
              <div className="text-xs text-white/70">Network</div>
              <div className="text-sm text-white">{cryptoInvoice?.network || 'Bitcoin'}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                <div className="text-xs text-white/70">Amount</div>
                <div className="text-sm text-white">
                  {cryptoAmountLoading
                    ? 'Loading…'
                    : `${cryptoInvoice?.amount || '0.00000000'} ${cryptoInvoice?.currency || 'BTC'}`}
                </div>
              </div>
              <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                <div className="text-xs text-white/70">Address</div>
                <div className="text-sm text-white font-mono truncate" title={cryptoInvoice?.address}>{cryptoInvoice?.address}</div>
              </div>
            </div>
            {Number.isFinite(cryptoInvoice?.usd_total) && (cryptoInvoice?.price_usd || cryptoAmountLoading) ? (
              <div className="text-xs text-white/70">
                ≈ ${Number(cryptoInvoice.usd_total).toFixed(2)} USD • Rate: 1 BTC = {cryptoAmountLoading ? 'Loading…' : `$${Number(cryptoInvoice.price_usd).toLocaleString()} USD`}
              </div>
            ) : null}
            {(() => {
              const addr = cryptoInvoice?.address || '';
              const amt = cryptoInvoice?.amount || '';
              const cur = (cryptoInvoice?.currency || 'BTC').toUpperCase();
              // Build a standard crypto URI (bitcoin:address?amount=...)
              // For demo purposes we keep the scheme as 'bitcoin:' for BTC-like flows.
              const scheme = cur === 'BTC' ? 'bitcoin' : cur.toLowerCase();
              const uri = addr ? `${scheme}:${addr}${amt ? `?amount=${amt}` : ''}` : '';
              const qrApi = uri ? `https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=${encodeURIComponent(uri)}` : '';
              return (
                <div className="rounded-lg border border-white/10 bg-white/5 p-4 flex flex-col items-center justify-center gap-2">
                  {qrApi ? (
                    <img src={qrApi} alt="Payment QR" className="w-44 h-44 rounded-md bg-white" />
                  ) : (
                    <div className="w-40 h-40 bg-gradient-to-br from-slate-300 to-slate-100 rounded-md shadow-inner" />
                  )}
                  {uri && (
                    <a
                      href={uri}
                      className="text-xs text-cyan-200 hover:text-cyan-100 underline"
                      title="Open with your wallet"
                    >Open in wallet</a>
                  )}
                </div>
              );
            })()}
            <div className="flex items-center justify-end gap-2 pt-2">
              <button onClick={() => { setCryptoOpen(false); setCryptoAmountLoading(false); setStatusMsg('Crypto payment cancelled.'); }} className="group border-2 border-white/20 hover:border-white/40 bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white px-5 py-2 rounded-full font-semibold text-sm transition-all duration-300">Cancel</button>
              <button onClick={async () => { setCryptoOpen(false); await placeOrder(); }} className="group relative bg-gradient-to-r from-yellow-500 to-amber-600 hover:from-yellow-400 hover:to-amber-500 text-black px-5 py-2 rounded-full font-semibold text-sm transition-all duration-300 transform hover:scale-105 hover:shadow-xl">I've sent payment</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
